import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SeoHead, { SITE_URL } from './seo/SeoHead';
import {
  ArrowUpRight,
  BadgeCheck,
  Bike,
  Camera,
  Car,
  CarFront,
  Caravan,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  FileCheck2,
  Gauge,
  Landmark,
  MapPin,
  MessageCircle,
  Navigation,
  ScanLine,
  ShieldCheck,
  Star,
  Truck,
  Wrench,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import '../landing.css';
import '../native-motion.css';

const copy = {
  en: {
    metaTitle: 'Al Professor | Certified Car Inspection Center in Abu Dhabi, Dubai & UAE',
    metaDescription: 'Al Professor is an independent, certified car inspection center serving Abu Dhabi, Dubai and the wider UAE. Get a detailed report with photo and video evidence before you buy, sell or repair a vehicle.',
    metaKeywords: 'car inspection Abu Dhabi, car inspection Dubai, vehicle inspection UAE, pre-purchase car inspection, mobile car inspection UAE, certified car inspection center',
    eyebrow: 'Independent vehicle inspection · Abu Dhabi',
    titleTop: 'Know the car.',
    titleAccent: 'Own the decision.',
    heroBody: 'A meticulous inspection that turns hidden details into a clear, confident buying decision.',
    book: 'Book an inspection',
    visit: 'Get directions',
    scroll: 'Discover the process',
    trust: [
      ['260+', 'inspection points'],
      ['Photo + video', 'evidence included'],
      ['On-site', 'mobile inspection'],
      ['Clear', 'digital report'],
    ],
    introEyebrow: 'Clarity before commitment',
    introTitle: 'We inspect what the eye can’t see.',
    introBody: 'From chassis and paint to diagnostics and road behavior, every finding is documented in plain language—so you can negotiate, walk away, or buy with confidence.',
    pillars: [
      ['Independent judgement', 'Our findings serve your decision—not the sale.'],
      ['Evidence, not opinions', 'Photos, videos and measured results support every important note.'],
      ['One complete picture', 'Mechanical, structural, electrical and cosmetic checks in one report.'],
    ],
    servicesEyebrow: 'What we inspect',
    servicesTitle: 'One center. Every critical detail.',
    servicesBody: 'Choose a focused service or let us build a complete view of the vehicle.',
    services: [
      ['Pre-purchase inspection', 'Buy with a complete understanding of the vehicle’s condition.', 'The complete check', 'pre-purchase-inspections'],
      ['Pre-repair diagnostics', 'Identify the real fault before approving repair work.', 'Find the cause', 'pre-repair-diagnostics'],
      ['Post-repair verification', 'Confirm that repairs were completed correctly and safely.', 'Verify the work', 'post-repair-verification'],
      ['Road simulation testing', 'Reveal suspension, steering and moving-mechanical issues.', 'Feel the difference', 'road-simulation-testing'],
      ['Vehicle appraisals', 'Documented condition and valuation support for banks.', 'Built for decisions', 'vehicle-appraisals-for-banks'],
      ['Car repair service', 'Professional maintenance and repair informed by accurate diagnosis.', 'Repair with clarity', 'car-repair-service'],
    ],
    vehicleTypesEyebrow: 'All vehicle types',
    vehicleTypesTitle: 'Vehicle types we inspect',
    vehicleTypes: ['Sedan', 'SUV / 4x4', 'Pickup truck', 'Van / light commercial', 'Motorcycle'],
    processEyebrow: 'How it works',
    processTitle: 'From arrival to answers.',
    processVisualLabel: 'Digital inspection',
    process: [
      ['01', 'Tell us about the car', 'Share the vehicle and location. We’ll recommend the right level of inspection.'],
      ['02', 'We inspect every system', 'A technician works through structural, mechanical, electrical and cosmetic checks.'],
      ['03', 'See the whole story', 'Receive a clear report with findings, media and practical next steps.'],
    ],
    packagesEyebrow: 'Inspection paths',
    packagesTitle: 'The right depth for your decision.',
    packages: [
      ['Comprehensive', 'The full picture before purchase', ['Up to 260+ inspection points', 'Computer diagnostics', 'Chassis, body and paint', 'Road and underbody checks']],
      ['Third-party', 'An independent second opinion', ['Repair quality review', 'Condition verification', 'Clear technical findings']],
      ['Fault diagnosis', 'Focused answers when something feels wrong', ['Computer scan', 'Mechanical diagnosis', 'Recommended next steps']],
    ],
    recommended: 'Most complete',
    choose: 'Ask about this inspection',
    mobileEyebrow: 'Inspection at your location',
    mobileTitle: 'The workshop comes to you.',
    mobileBody: 'Can’t bring the vehicle in? Our mobile team can inspect it at the seller’s location across the UAE and send you the findings wherever you are.',
    mobilePoints: ['Convenient scheduling', 'Professional inspection team', 'Photo and video documentation'],
    mobileCta: 'Request mobile inspection',
    reviewsEyebrow: 'Reputation built on detail',
    mapOrigin: 'Workshop',
    mapCity: 'Abu Dhabi',
    mapLabel: 'Mobile inspection team',
    reviewsTitle: 'Five-star care. Zero guesswork.',
    reviewsBody: 'Drivers choose Al Professor when the decision matters. Our job is to make the condition of the car understandable, documented and impossible to overlook.',
    readReviews: 'Read our Google reviews',
    finalEyebrow: 'Before you sign, know.',
    googleReviews: 'Google Reviews',
    finalTitle: 'Your next car deserves a second look.',
    finalBody: 'Send us the vehicle details. We’ll help you choose the right inspection and arrange the rest.',
    call: 'Call us',
  },
  ar: {
    metaTitle: 'البروفيسور لفحص السيارات | مركز فحص سيارات معتمد في أبوظبي ودبي',
    metaDescription: 'البروفيسور مركز فحص سيارات مستقل ومعتمد في أبوظبي ودبي وباقي إمارات الدولة. احصل على تقرير فحص مفصل بالصور والفيديو قبل شراء أو بيع أو إصلاح سيارتك.',
    metaKeywords: 'فحص سيارات أبوظبي, فحص سيارات دبي, فحص سيارات الإمارات, فحص سيارة قبل الشراء, فحص سيارات متنقل, مركز فحص سيارات معتمد',
    eyebrow: 'فحص مركبات مستقل · أبوظبي',
    titleTop: 'اعرف السيارة.',
    titleAccent: 'واملك القرار.',
    heroBody: 'فحص دقيق يحوّل التفاصيل الخفية إلى قرار شراء واضح وواثق.',
    book: 'احجز موعد فحص',
    visit: 'اتجه إلى المركز',
    scroll: 'اكتشف آلية الفحص',
    trust: [
      ['+260', 'نقطة فحص'],
      ['صور + فيديو', 'توثيق شامل'],
      ['في موقعك', 'فحص متنقل'],
      ['واضح', 'تقرير رقمي'],
    ],
    introEyebrow: 'وضوح قبل الالتزام',
    introTitle: 'نفحص ما لا تراه العين.',
    introBody: 'من الشاصي والطلاء إلى الكمبيوتر وأداء السيارة على الطريق، نوثّق كل ملاحظة بلغة واضحة لتفاوض بثقة أو تنسحب أو تشتري وأنت مطمئن.',
    pillars: [
      ['رأي فني مستقل', 'نتائجنا تخدم قرارك أنت، لا عملية البيع.'],
      ['أدلة لا انطباعات', 'صور وفيديو وقياسات تدعم كل ملاحظة مهمة.'],
      ['صورة متكاملة', 'فحص ميكانيكي وهيكلي وكهربائي وتجميلي في تقرير واحد.'],
    ],
    servicesEyebrow: 'ماذا نفحص',
    servicesTitle: 'مركز واحد. كل التفاصيل المهمة.',
    servicesBody: 'اختر خدمة محددة أو دعنا نبني لك صورة متكاملة عن حالة المركبة.',
    services: [
      ['فحص ما قبل الشراء', 'اعرف حالة السيارة كاملة قبل أن تدفع.', 'الفحص المتكامل', 'pre-purchase-inspections'],
      ['تشخيص ما قبل الإصلاح', 'حدّد العطل الحقيقي قبل الموافقة على الإصلاح.', 'اكتشف السبب', 'pre-repair-diagnostics'],
      ['فحص ما بعد الإصلاح', 'تأكد أن الإصلاح تم بالشكل الصحيح والآمن.', 'تحقق من العمل', 'post-repair-verification'],
      ['محاكاة الطريق', 'اكشف مشاكل التعليق والتوجيه والأجزاء المتحركة.', 'اختبر الفرق', 'road-simulation-testing'],
      ['تقييم المركبات', 'تقرير حالة وقيمة موثق للبنوك وجهات التمويل.', 'معلومات للقرار', 'vehicle-appraisals-for-banks'],
      ['صيانة وإصلاح السيارات', 'صيانة احترافية تبدأ بتشخيص دقيق.', 'إصلاح بوضوح', 'car-repair-service'],
    ],
    vehicleTypesEyebrow: 'كل أنواع المركبات',
    vehicleTypesTitle: 'أنواع المركبات التي نفحصها',
    vehicleTypes: ['سيارة صالون', 'دفع رباعي (SUV)', 'شاحنة بيك أب', 'فان / نقل خفيف', 'دراجة نارية'],
    processEyebrow: 'كيف نعمل',
    processTitle: 'من وصول السيارة إلى وضوح الإجابة.',
    processVisualLabel: 'فحص رقمي',
    process: [
      ['01', 'عرّفنا على السيارة', 'أرسل بيانات المركبة وموقعها وسنرشح لك مستوى الفحص المناسب.'],
      ['02', 'نفحص كل نظام', 'يفحص الفني الجوانب الهيكلية والميكانيكية والكهربائية والتجميلية.'],
      ['03', 'شاهد القصة كاملة', 'استلم تقريراً واضحاً بالنتائج والصور والخطوات العملية التالية.'],
    ],
    packagesEyebrow: 'مسارات الفحص',
    packagesTitle: 'العمق المناسب لكل قرار.',
    packages: [
      ['الفحص الشامل', 'الصورة الكاملة قبل الشراء', ['أكثر من 260 نقطة فحص', 'فحص كمبيوتر', 'الشاصي والهيكل والطلاء', 'فحص الطريق وأسفل المركبة']],
      ['فحص طرف ثالث', 'رأي فني مستقل ومحايد', ['تقييم جودة الإصلاح', 'التحقق من الحالة', 'ملاحظات فنية واضحة']],
      ['تشخيص الأعطال', 'إجابات مركزة عندما تشعر بوجود مشكلة', ['فحص كمبيوتر', 'تشخيص ميكانيكي', 'خطوات إصلاح مقترحة']],
    ],
    recommended: 'الأكثر شمولاً',
    choose: 'استفسر عن هذا الفحص',
    mobileEyebrow: 'الفحص في موقعك',
    mobileTitle: 'الورشة تأتي إليك.',
    mobileBody: 'لا تستطيع إحضار السيارة؟ يصل فريقنا المتنقل إلى موقع البائع داخل الإمارات ويرسل لك النتائج أينما كنت.',
    mobilePoints: ['موعد يناسبك', 'فريق فحص محترف', 'توثيق بالصور والفيديو'],
    mobileCta: 'اطلب الفحص المتنقل',
    reviewsEyebrow: 'سمعة مبنية على الدقة',
    reviewsTitle: 'عناية خمس نجوم. بلا تخمين.',
    mapOrigin: 'الورشة',
    mapCity: 'أبوظبي',
    mapLabel: 'فريق الفحص المتنقل',
    reviewsBody: 'يختار السائقون البروفيسور عندما يكون القرار مهماً. مهمتنا أن نجعل حالة السيارة واضحة وموثقة ولا يمكن تجاهلها.',
    readReviews: 'اقرأ تقييماتنا على Google',
    finalEyebrow: 'اعرف قبل أن توقّع',
    finalTitle: 'سيارتك القادمة تستحق نظرة ثانية.',
    googleReviews: 'تقييمات Google',
    finalBody: 'أرسل لنا بيانات المركبة وسنساعدك في اختيار الفحص المناسب وترتيب الباقي.',
    call: 'اتصل بنا',
  },
};

const serviceIcons = [ScanLine, Gauge, BadgeCheck, Navigation, Landmark, Wrench];
const pillarIcons = [ShieldCheck, Camera, FileCheck2];
const vehicleTypeIcons = [Car, CarFront, Truck, Caravan, Bike];

const Reveal = ({ children, className = '', delay = 0 }) => {
  const revealRef = useRef(null);

  useEffect(() => {
    const node = revealRef.current;
    if (!node) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      node.classList.add('is-visible');
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        node.classList.add('is-visible');
        observer.disconnect();
      }
    }, { threshold: 0.16, rootMargin: '0px 0px -4% 0px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={revealRef} className={`native-reveal ${className}`} style={{ '--reveal-delay': `${delay}s` }}>
      {children}
    </div>
  );
};

const WhatsAppLink = ({ children, className = '' }) => (
  <a
    href="https://wa.me/971561868236?text=Hello%2C%20I%20would%20like%20to%20book%20a%20vehicle%20inspection."
    target="_blank"
    rel="noopener noreferrer"
    className={className}
  >
    {children}
  </a>
);

const VehicleTypeCarousel = ({ items, isRTL }) => {
  const trackRef = useRef(null);

  const scrollByCard = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector('.vehicle-card');
    const step = (card ? card.offsetWidth + 18 : 240) * direction * (isRTL ? -1 : 1);
    track.scrollBy({ left: step, behavior: 'smooth' });
  };

  return (
    <div className="vehicle-carousel">
      <button type="button" className="vehicle-nav-btn vehicle-nav-prev" onClick={() => scrollByCard(-1)} aria-label="Previous">
        <ChevronLeft size={20} />
      </button>
      <div className="vehicle-track" ref={trackRef}>
        {items.map(({ label, Icon }) => (
          <div className="vehicle-card" key={label}>
            <span className="vehicle-card-icon"><Icon size={34} /></span>
            <span className="vehicle-card-label">{label}</span>
          </div>
        ))}
      </div>
      <button type="button" className="vehicle-nav-btn vehicle-nav-next" onClick={() => scrollByCard(1)} aria-label="Next">
        <ChevronRight size={20} />
      </button>
    </div>
  );
};

const LandingPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'ar';
  const c = copy[lang];
  const isRTL = lang === 'ar';
  const heroRef = useRef(null);
  const heroMediaRef = useRef(null);
  const heroCopyRef = useRef(null);
  const scrollCueRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    const media = heroMediaRef.current;
    const content = heroCopyRef.current;
    const cue = scrollCueRef.current;
    if (!hero || !media || !content || !cue) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let animationFrame = 0;

    const renderMotion = () => {
      animationFrame = 0;
      const travel = Math.max(hero.offsetHeight - window.innerHeight, 1);
      const progress = Math.min(Math.max(-hero.getBoundingClientRect().top / travel, 0), 1);
      const scale = reduceMotion ? 1.04 : 1.04 + (progress * 0.12);
      const mediaY = reduceMotion ? 0 : progress * 90;
      const contentY = reduceMotion ? 0 : Math.min(progress / 0.75, 1) * -90;
      const opacity = reduceMotion ? 1 : Math.max(1 - (progress / 0.72), 0);

      media.style.transform = `translate3d(0, ${mediaY}px, 0) scale(${scale})`;
      content.style.transform = `translate3d(0, ${contentY}px, 0)`;
      content.style.opacity = String(opacity);
      cue.style.opacity = String(opacity);
    };

    const queueMotion = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(renderMotion);
    };

    renderMotion();
    window.addEventListener('scroll', queueMotion, { passive: true });
    window.addEventListener('resize', queueMotion, { passive: true });
    return () => {
      window.removeEventListener('scroll', queueMotion);
      window.removeEventListener('resize', queueMotion);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <div className="landing-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <SeoHead
        path=""
        title={c.metaTitle}
        description={c.metaDescription}
        keywords={c.metaKeywords}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AutomotiveBusiness',
          name: 'Al Professor Car Inspection',
          image: `${SITE_URL}/inspection-car.png`,
          telephone: '+971561868236',
          priceRange: 'AED',
          url: `${SITE_URL}/${lang}/`,
          areaServed: ['Abu Dhabi', 'Dubai', 'United Arab Emirates'],
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Abu Dhabi',
            addressCountry: 'AE',
          },
        }}
      />

      <section ref={heroRef} className="cinematic-hero" aria-labelledby="hero-title">
        <div className="hero-sticky">
          <div ref={heroMediaRef} className="hero-media">
            <video autoPlay muted loop playsInline preload="metadata" aria-hidden="true">
              <source src="/hero_video.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="hero-vignette" />
          <div className="hero-grain" />

          <div ref={heroCopyRef} className="hero-copy site-container">
            <p className="eyebrow hero-eyebrow"><span />{c.eyebrow}</p>
            <h1 id="hero-title">
              <span>{c.titleTop}</span>
              <em>{c.titleAccent}</em>
            </h1>
            <p className="hero-lead">{c.heroBody}</p>
            <div className="hero-actions">
              <WhatsAppLink className="button button-primary">
                <MessageCircle size={19} />
                {c.book}
                <ArrowUpRight size={18} />
              </WhatsAppLink>
              <a className="button button-ghost" href="https://maps.app.goo.gl/pCJaz6oGmEgim6gk6" target="_blank" rel="noopener noreferrer">
                <MapPin size={19} />
                {c.visit}
              </a>
            </div>
          </div>

          <a ref={scrollCueRef} className="scroll-cue" href="#intro">
            <span>{c.scroll}</span>
            <ChevronDown size={18} />
          </a>
        </div>
      </section>

      <main className="landing-content">
        <section className="trust-rail" aria-label="Inspection highlights">
          <div className="site-container trust-grid">
            {c.trust.map(([value, label]) => (
              <div className="trust-item" key={label}>
                <strong>{value}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </section>

        <section id="intro" className="section intro-section">
          <div className="site-container intro-grid">
            <Reveal className="section-heading">
              <p className="eyebrow dark"><span />{c.introEyebrow}</p>
              <h2>{c.introTitle}</h2>
            </Reveal>
            <Reveal className="intro-copy" delay={0.08}>
              <p>{c.introBody}</p>
            </Reveal>
          </div>
          <div className="site-container pillar-grid">
            {c.pillars.map(([title, body], index) => {
              const Icon = pillarIcons[index];
              return (
                <Reveal className="pillar" delay={index * 0.08} key={title}>
                  <span className="pillar-icon"><Icon size={21} /></span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </Reveal>
              );
            })}
          </div>
        </section>

        <section id="services-section" className="section services-section">
          <div className="site-container">
            <Reveal className="section-heading services-heading">
              <div>
                <p className="eyebrow light"><span />{c.servicesEyebrow}</p>
                <h2>{c.servicesTitle}</h2>
              </div>
              <p>{c.servicesBody}</p>
            </Reveal>

            <div className="services-grid">
              {c.services.map(([title, body, label, route], index) => {
                const Icon = serviceIcons[index];
                return (
                  <Reveal className={`service-card service-card-${index + 1}`} delay={index * 0.045} key={title}>
                    <Link to={`/${lang}/services/${route}`} aria-label={title}>
                      <div className="service-topline">
                        <span className="service-number">0{index + 1}</span>
                        <span className="service-icon"><Icon size={24} /></span>
                      </div>
                      <div>
                        <h3>{title}</h3>
                        <p>{body}</p>
                      </div>
                      <span className="service-link">{label}<ArrowUpRight size={17} /></span>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section className="section vehicle-types-section">
          <div className="site-container">
            <Reveal className="section-heading centered">
              <p className="eyebrow dark"><span />{c.vehicleTypesEyebrow}</p>
              <h2>{c.vehicleTypesTitle}</h2>
            </Reveal>
            <VehicleTypeCarousel
              isRTL={isRTL}
              items={c.vehicleTypes.map((label, index) => ({ label, Icon: vehicleTypeIcons[index] }))}
            />
          </div>
        </section>

        <section className="section process-section">
          <div className="site-container process-layout">
            <Reveal className="process-intro">
              <p className="eyebrow dark"><span />{c.processEyebrow}</p>
              <h2>{c.processTitle}</h2>
              <div className="process-visual" aria-hidden="true">
                <img src="/inspection-car.png" alt="" className="process-car-image" />
                <span className="process-scan-label"><ScanLine size={17} />{c.processVisualLabel}</span>
              </div>
            </Reveal>
            <div className="process-steps">
              {c.process.map(([number, title, body], index) => (
                <Reveal className="process-step" delay={index * 0.1} key={number}>
                  <span>{number}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="section packages-section">
          <div className="site-container">
            <Reveal className="section-heading centered">
              <p className="eyebrow light"><span />{c.packagesEyebrow}</p>
              <h2>{c.packagesTitle}</h2>
            </Reveal>
            <div className="packages-grid">
              {c.packages.map(([title, body, points], index) => (
                <Reveal className={`package-card ${index === 0 ? 'featured' : ''}`} delay={index * 0.08} key={title}>
                  {index === 0 && <span className="package-badge">{c.recommended}</span>}
                  <span className="package-index">0{index + 1}</span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                  <ul>
                    {points.map((point) => <li key={point}><Check size={17} />{point}</li>)}
                  </ul>
                  <WhatsAppLink className="package-link">{c.choose}<ArrowUpRight size={17} /></WhatsAppLink>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="section mobile-section">
          <div className="site-container mobile-card">
            <div className="mobile-map" aria-hidden="true">
              <span className="map-grid" />
              <svg className="map-route-svg" viewBox="0 0 600 520" preserveAspectRatio="none" focusable="false">
                <path className="map-route-path" d="M 90 120 C 220 80, 320 220, 300 320 S 460 420, 500 400" />
              </svg>
              <span className="map-node map-node-start">
                <span className="map-node-dot" />
                <span className="map-node-tag">{c.mapOrigin}</span>
              </span>
              <span className="map-node map-node-end">
                <span className="map-node-pulse" />
                <span className="map-node-pin"><MapPin size={16} /></span>
              </span>
              <div className="map-location-card">
                <span className="map-location-icon"><MapPin size={20} /></span>
                <div><strong>{c.mapCity}</strong><span>{c.mapLabel}</span></div>
              </div>
            </div>
            <Reveal className="mobile-copy">
              <p className="eyebrow light"><span />{c.mobileEyebrow}</p>
              <h2>{c.mobileTitle}</h2>
              <p>{c.mobileBody}</p>
              <ul>
                {c.mobilePoints.map((point, index) => {
                  const Icon = [Clock3, ShieldCheck, Camera][index];
                  return <li key={point}><Icon size={19} />{point}</li>;
                })}
              </ul>
              <WhatsAppLink className="button button-primary">
                <MessageCircle size={19} />{c.mobileCta}<ArrowUpRight size={18} />
              </WhatsAppLink>
            </Reveal>
          </div>
        </section>

        <section className="section reviews-section">
          <div className="site-container reviews-grid">
            <Reveal className="review-score">
              <div className="score-value">5.0</div>
              <div className="stars" aria-label="5 out of 5 stars">
                {[0, 1, 2, 3, 4].map((star) => <Star key={star} size={18} fill="currentColor" />)}
              </div>
              <span>{c.googleReviews}</span>
            </Reveal>
            <Reveal className="review-copy" delay={0.1}>
              <p className="eyebrow dark"><span />{c.reviewsEyebrow}</p>
              <h2>{c.reviewsTitle}</h2>
              <p>{c.reviewsBody}</p>
              <a href="https://www.google.com/search?q=Al+Professor+Car+Inspection+reviews" target="_blank" rel="noopener noreferrer">
                {c.readReviews}<ArrowUpRight size={17} />
              </a>
            </Reveal>
          </div>
        </section>

        <section className="final-cta">
          <div className="cta-glow" />
          <Reveal className="site-container final-cta-inner">
            <p className="eyebrow hero-eyebrow"><span />{c.finalEyebrow}</p>
            <h2>{c.finalTitle}</h2>
            <p>{c.finalBody}</p>
            <div className="hero-actions">
              <WhatsAppLink className="button button-primary"><MessageCircle size={19} />{c.book}<ArrowUpRight size={18} /></WhatsAppLink>
              <a className="button button-ghost" href="tel:+971561868236">{c.call}<bdi dir="ltr" className="phone-number">+971 56 186 8236</bdi></a>
            </div>
          </Reveal>
        </section>
      </main>
    </div>
  );
};

export default LandingPage;
